"use client";

import { useId, useMemo, useState } from "react";
import { Icon } from "@/lib/icons";

const CASE_OPTIONS = [
  { label: "UPPERCASE", value: "uppercase" },
  { label: "lowercase", value: "lowercase" },
  { label: "Title Case", value: "title" },
  { label: "Sentence case", value: "sentence" },
  { label: "camelCase", value: "camel" },
  { label: "PascalCase", value: "pascal" },
  { label: "snake_case", value: "snake" },
  { label: "kebab-case", value: "kebab" },
] as const;

type CaseValue = (typeof CASE_OPTIONS)[number]["value"];

/**
 * Splits text into words, handling three kinds of boundaries at once:
 * whitespace/punctuation separators, AND camelCase/PascalCase transitions
 * (so "getUserById" splits into ["get", "User", "By", "Id"], not one blob).
 * This is what makes converting an existing identifier's case actually work.
 */
function splitWords(text: string): string[] {
  return text
    .trim()
    // Insert a boundary between a lowercase/digit and an uppercase letter: "aB" -> "a B"
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    // Insert a boundary between consecutive uppercase followed by lowercase: "ABCd" -> "AB Cd"
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    // Now split on any run of non-alphanumeric characters (spaces, _, -, punctuation)
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean);
}

function toTitleCase(text: string) {
  return text.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Capitalizes the first letter of each sentence WITHOUT lowercasing
 * everything else first, so existing acronyms (JSON, API, U.S.) survive.
 */
function toSentenceCase(text: string) {
  let result = "";
  let capitalizeNext = true;

  for (const char of text) {
    if (capitalizeNext && /[a-zA-Z]/.test(char)) {
      result += char.toUpperCase();
      capitalizeNext = false;
    } else {
      result += char;
    }
    if (/[.!?]/.test(char)) {
      capitalizeNext = true;
    }
  }
  return result;
}

function toCamelCase(text: string) {
  const words = splitWords(text);
  if (words.length === 0) return "";
  return words
    .map((word, index) => {
      const normalized = word.toLowerCase();
      return index === 0 ? normalized : normalized.charAt(0).toUpperCase() + normalized.slice(1);
    })
    .join("");
}

function toPascalCase(text: string) {
  return splitWords(text)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join("");
}

function toSnakeCase(text: string) {
  return splitWords(text)
    .map((word) => word.toLowerCase())
    .join("_");
}

function toKebabCase(text: string) {
  return splitWords(text)
    .map((word) => word.toLowerCase())
    .join("-");
}

export default function CaseConverter({ toolName }: { toolName: string }) {
  const idPrefix = useId();
  const [input, setInput] = useState("");
  const [activeCase, setActiveCase] = useState<CaseValue>("uppercase");
  const [copyLabel, setCopyLabel] = useState("Copy result");

  const output = useMemo(() => {
    if (!input) return "";

    switch (activeCase) {
      case "lowercase":
        return input.toLowerCase();
      case "title":
        return toTitleCase(input);
      case "sentence":
        return toSentenceCase(input);
      case "camel":
        return toCamelCase(input);
      case "pascal":
        return toPascalCase(input);
      case "snake":
        return toSnakeCase(input);
      case "kebab":
        return toKebabCase(input);
      case "uppercase":
      default:
        return input.toUpperCase();
    }
  }, [activeCase, input]);

  const stats = useMemo(() => {
    const trimmed = input.trim();
    return {
      words: trimmed ? trimmed.split(/\s+/).length : 0,
      chars: input.length,
    };
  }, [input]);

  async function copyResult() {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopyLabel("Copied!");
    } catch {
      setCopyLabel("Copy failed");
    }
    window.setTimeout(() => setCopyLabel("Copy result"), 1500);
  }

  function clearAll() {
    setInput("");
  }

  return (
    <>
      <div>
        <div className="mb-2 flex items-center justify-between">
          <label htmlFor={`${idPrefix}-input`} className="text-sm font-medium text-slate-700">
            Your text
          </label>
          <button
            type="button"
            onClick={clearAll}
            disabled={!input}
            className="text-xs font-semibold text-slate-500 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Clear
          </button>
        </div>
        <textarea
          id={`${idPrefix}-input`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          rows={6}
          placeholder="Type or paste your text here… works with plain sentences or identifiers like getUserById."
          className="w-full rounded-xl border border-slate-300 p-4 text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
        />
        <p className="mt-1 text-xs text-slate-500">
          {stats.words} word{stats.words === 1 ? "" : "s"} · {stats.chars} character{stats.chars === 1 ? "" : "s"}
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Case style">
          {CASE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={activeCase === option.value}
              onClick={() => setActiveCase(option.value)}
              className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                activeCase === option.value
                  ? "bg-indigo-600 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div>
          <label htmlFor={`${idPrefix}-output`} className="mb-2 block text-sm font-medium text-slate-700">
            Converted text
          </label>
          <textarea
            id={`${idPrefix}-output`}
            value={output}
            readOnly
            rows={6}
            placeholder="Your converted result will appear here…"
            className="w-full rounded-xl border border-slate-300 bg-slate-50 p-4 text-slate-900 outline-none"
          />
        </div>
      </div>

      <button
        type="button"
        onClick={() => void copyResult()}
        disabled={!output}
        className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300"
      >
        <Icon name="check" className="h-4 w-4" />
        {copyLabel}
      </button>
    </>
  );
}